'use client';

import BodyLink from '@jouzen/ecom-components/v3/BodyLink';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { TypographyRhythm } from '@jouzen/ecom-components/v3/TypographyRhythm';
import { useLocale } from 'next-intl';
import type { ReactNode } from 'react';

import LegalList from '@/app/components/LegalComponents/LegalList';
import LegalParagraph from '@/app/components/LegalComponents/LegalParagraph';
import RichText from '@/app/components/RichText';

interface LinkProps {
  readonly chunks: ReactNode;
  readonly selectedLocale: string;
}

const gdprLink = ({ chunks, selectedLocale }: LinkProps) => (
  <BodyLink
    external
    href={`https://eur-lex.europa.eu/legal-content/${selectedLocale.toUpperCase()}/TXT/PDF/?uri=CELEX:32016R0679&from=${selectedLocale.toUpperCase()}`}
    target="_blank"
    rel="noreferrer"
  >
    {chunks}
  </BodyLink>
);

const icoLink = (chunks: ReactNode) => (
  <BodyLink
    external
    href={`https://ico.org.uk/for-organisations/guide-to-the-general-data-protection-regulation-gdpr/individual-rights/`}
    target="_blank"
    rel="noreferrer"
  >
    {chunks}
  </BodyLink>
);

const Content = () => {
  const selectedLocale = useLocale();

  return (
    <LayoutGrid data-cy="recruitment-privacy-notice">
      <TypographyRhythm className="col-main">
        <Typography
          Element="h1"
          variant="h2"
          className="mb-4"
          data-cy="privacy-notice-title"
        >
          <RichText messageKey="recruitment_privacy_notice_title" />
        </Typography>
        <LegalParagraph tx="recruitment_privacy_notice_p1" />
        <LegalParagraph tx="recruitment_privacy_notice_p2" />
        <LegalParagraph tx="recruitment_privacy_notice_p3" />
        <LegalParagraph tx="recruitment_privacy_notice_p4" />
        <LegalParagraph tx="recruitment_privacy_notice_p5" />

        <Typography Element="h2" variant="h3" className="mt-8 mb-4">
          <RichText messageKey="recruitment_personal_info_title" />
        </Typography>
        <Typography Element="h3" variant="h4" className="mb-4">
          <RichText messageKey="recruitment_personal_info_subtitle_1" />
        </Typography>
        <LegalParagraph tx="recruitment_personal_info_p1" />
        <LegalList indent="double" className="list-outside list-disc">
          <RichText messageKey="recruitment_personal_info_p1_list" />
        </LegalList>
        <Typography Element="h3" variant="h4" className="mb-4">
          <RichText messageKey="recruitment_personal_info_subtitle_2" />
        </Typography>
        <LegalParagraph tx="recruitment_personal_info_p2" />
        <LegalParagraph tx="recruitment_personal_info_p3" />
        <LegalParagraph tx="recruitment_personal_info_p4" />

        <Typography Element="h2" variant="h3" className="mb-4">
          <RichText messageKey="recruitment_info_uses_title" />
        </Typography>
        <Typography Element="h3" variant="h4" className="mb-4">
          <RichText messageKey="recruitment_info_uses_subtitle_1" />
        </Typography>
        <LegalParagraph tx="recruitment_info_uses_p1" />
        <Typography Element="h3" variant="h4" className="mb-4">
          <RichText messageKey="recruitment_info_uses_subtitle_2" />
        </Typography>
        <LegalParagraph tx="recruitment_info_uses_p2" />
        <LegalList indent="double" className="list-outside list-disc">
          <RichText messageKey="recruitment_info_uses_p2_list" />
        </LegalList>
        <Typography Element="h3" variant="h4" className="mb-4">
          <RichText messageKey="recruitment_info_uses_subtitle_3" />
        </Typography>
        <LegalParagraph tx="recruitment_info_uses_p3" />

        <Typography Element="h2" variant="h3" className="mb-4">
          <RichText messageKey="recruitment_info_disclosure_title" />
        </Typography>
        <LegalParagraph tx="recruitment_info_disclosure_p1" />
        <LegalParagraph tx="recruitment_info_disclosure_p2" />
        <LegalParagraph tx="recruitment_info_disclosure_p3" />

        <Typography Element="h2" variant="h3" className="mb-4">
          <RichText messageKey="recruitment_personal_data_storage_title" />
        </Typography>
        <Typography Element="h3" variant="h4" className="mb-4">
          <RichText messageKey="recruitment_personal_data_storage_subtitle_1" />
        </Typography>
        <LegalParagraph tx="recruitment_personal_data_storage_p1" />
        <LegalParagraph tx="recruitment_personal_data_storage_p2" />
        <LegalParagraph tx="recruitment_personal_data_storage_p3" />
        <Typography Element="h3" variant="h4" className="mb-4">
          <RichText messageKey="recruitment_personal_data_storage_subtitle_2" />
        </Typography>
        <LegalParagraph tx="recruitment_personal_data_storage_p4" />
        <LegalParagraph tx="recruitment_personal_data_storage_p5" />
        <LegalParagraph tx="recruitment_personal_data_storage_p6" />
        <LegalParagraph tx="recruitment_personal_data_storage_p7" />
        <Typography Element="h3" variant="h4" className="mb-4">
          <RichText messageKey="recruitment_personal_data_storage_subtitle_3" />
        </Typography>
        <LegalParagraph tx="recruitment_personal_data_storage_p8" />
        <LegalParagraph tx="recruitment_personal_data_storage_p9" />
        <LegalList indent="double" className="list-outside list-disc">
          <RichText messageKey="recruitment_personal_data_storage_p9_list" />
        </LegalList>
        <Typography Element="h3" variant="h4" className="mb-4">
          <RichText messageKey="recruitment_personal_data_storage_subtitle_4" />
        </Typography>
        <LegalParagraph
          tx="recruitment_personal_data_storage_p10"
          values={{
            link: (chunks: ReactNode) => gdprLink({ chunks, selectedLocale }),
          }}
        />
        <LegalList indent="double" className="list-outside list-disc">
          <RichText messageKey="recruitment_personal_data_storage_p10_list" />
        </LegalList>
        <LegalParagraph
          tx="recruitment_personal_data_storage_p11"
          values={{ link: icoLink }}
        />
        <LegalParagraph tx="recruitment_personal_data_storage_p12" />
        <LegalList indent="double" className="list-outside list-disc">
          <RichText messageKey="recruitment_personal_data_storage_p12_list" />
        </LegalList>

        <Typography Element="h2" variant="h3" className="mb-4">
          <RichText messageKey="recruitment_how_to_complain_title" />
        </Typography>
        <LegalParagraph tx="recruitment_how_to_complain_p1" />
        <LegalParagraph
          tx="recruitment_how_to_complain_p2"
          values={{
            link: (chunks: ReactNode) => gdprLink({ chunks, selectedLocale }),
          }}
        />

        <Typography Element="h2" variant="h3" className="mb-4">
          <RichText messageKey="recruitment_contact_title" />
        </Typography>
        <LegalParagraph tx="recruitment_contact_p1" />
      </TypographyRhythm>
    </LayoutGrid>
  );
};

export default Content;
